import { useMemo, useState } from 'react';

/**
 * Chip on/off state plus the row filter, for a working queue.
 *
 * Predicates run client-side by necessity, not preference: the compound rules
 * these queues need — `likelihood = High` **and** no consult booked — read JSON
 * inside a Communication payload, which no FHIR search parameter can express.
 * The date range scopes the query server-side; chips filter what came back.
 *
 * Active chips are ANDed. `resetKey` is the tab: switching tabs restores the
 * defaults rather than carrying one tab's chips onto another's population, which
 * would silently mis-describe the count.
 */
export interface ChipFilterDefinition<T> {
  id: string;
  label: string;
  /** On at first render. The Inactive chip is the case this exists for. */
  defaultOn?: boolean;
  /** Can be dismissed entirely, not just switched off. */
  removable?: boolean;
  predicate: (row: T) => boolean;
}

export interface ChipFilterState<T> {
  /** Definitions minus anything removed — what the bar should render. */
  definitions: ChipFilterDefinition<T>[];
  activeIds: string[];
  toggle: (id: string) => void;
  remove: (id: string) => void;
  apply: (rows: T[]) => T[];
}

function defaultActiveIds<T>(definitions: ChipFilterDefinition<T>[]): string[] {
  return definitions.filter((definition) => definition.defaultOn).map((definition) => definition.id);
}

export function useChipFilters<T>(definitions: ChipFilterDefinition<T>[], resetKey: string): ChipFilterState<T> {
  const [activeIds, setActiveIds] = useState<string[]>(() => defaultActiveIds(definitions));
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  // Render-phase reset, matching how PatientListPage already resets sorting on a
  // filter change. Doing it in an effect would leave one frame of stale
  // filtering, during which the count would describe the previous tab.
  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setActiveIds(defaultActiveIds(definitions));
    setRemovedIds([]);
  }

  const visibleDefinitions = useMemo(
    () => definitions.filter((definition) => !removedIds.includes(definition.id)),
    [definitions, removedIds]
  );

  const knownIds = useMemo(() => definitions.map((definition) => definition.id), [definitions]);

  return {
    definitions: visibleDefinitions,
    activeIds,
    toggle: (id: string) => {
      if (!knownIds.includes(id)) {
        return;
      }
      setActiveIds((current) => (current.includes(id) ? current.filter((v) => v !== id) : [...current, id]));
    },
    remove: (id: string) => {
      setRemovedIds((current) => (current.includes(id) ? current : [...current, id]));
      setActiveIds((current) => current.filter((v) => v !== id));
    },
    apply: (rows: T[]) => {
      const applied = visibleDefinitions.filter((definition) => activeIds.includes(definition.id));
      return applied.length === 0 ? rows : rows.filter((row) => applied.every((d) => d.predicate(row)));
    },
  };
}
